import { SegmentFieldItem } from 'utils/segment-field-item.interface';
import { countryLookup } from 'utils/lookups';
import {
  assetTypeLookup, representationLookup, fileTypeLookup, colorModeLookup, resolutionLookup,
  backgroundLookup, orientationViewLookup, assetUomLookup,
} from 'shared/digital-assets/digital-assets-lookups';
import { languageLookup } from 'shared/product/product-lookups';
import { DetailsControlType } from 'shared/details-page/details-control/details-control-type';
import patterns from 'utils/patterns';


export interface AssetFieldItem extends SegmentFieldItem {
  imageOnly?: boolean;
  errorMessage?: string;
  is_pag?: boolean; // indicate PAG asset type field
}

/* eslint-disable */
export const digitalAssetsSegmentData: Array<AssetFieldItem> = [
  {
    'field_map': 'assetType',
    'item_name': 'asset_type',
    'title': 'Asset type',
    'hint_detail': 'Indicates the asset type in relation to the item.',
    'hint_panel' : 'Select the asset type for this asset as it relates to the item.',
    'example': 'Photo-primary',
    'sequence': 1,
    'show_more': true,
    'lookup': assetTypeLookup,
  },
  {
    'field_map': 'representation',
    'item_name': 'representation',
    'title': 'Representation',
    'hint_detail': 'Indicates this photo is of the actual product corresponding to the part number.',
    'hint_panel' : 'Indicates this photo is of the actual product corresponding to the part number.',
    'example': 'Actual product',
    'sequence': 2,
    'show_more': true,
    'lookup': representationLookup
  },
  {
    'field_map': 'filesize',
    'item_name': 'file_size',
    'title': 'File size',
    'hint_detail': 'File size of digital asset as measured in Kilobytes (kb).',
    'hint_panel' : 'File size of digital asset as measured in Kilobytes (kb).',
    'example': '',
    'sequence': 3,
    'show_more': true,
    'lookup': null
  },
  {
    'field_map': 'fileType',
    'item_name': 'file_type',
    'title': 'File type',
    'hint_detail': 'File type of the digital asset uploaded into the system.',
    'hint_panel' : 'File type of the digital asset uploaded into the system.',
    'example': '',
    'sequence': 4,
    'show_more': true,
    'lookup': fileTypeLookup,
  },
  {
    'field_map': 'colorMode',
    'item_name': 'color_mode',
    'title': 'Color mode',
    'hint_detail': 'Digital asset color mode',
    'hint_panel' : 'Digital asset color mode',
    'example': '',
    'sequence': 5,
    'show_more': true,
    'lookup': colorModeLookup,
   'imageOnly': true,
  },
  {
    'field_map': 'resolution',
    'item_name': 'resolution',
    'title': 'Resolution',
    'hint_detail': 'DPI (dots per inch) of digital asset',
    'hint_panel' : 'DPI (dots per inch) of digital asset',
    'example': '',
    'sequence': 6,
    'show_more': true,
    'lookup': resolutionLookup,
    'imageOnly': true,
  },
  {
    'field_map': 'filename',
    'item_name': 'filename',
    'title': 'File name',
    'hint_detail': 'File name of digital asset as uploaded into system.',
    'hint_panel' : 'File name (including file extension) of digital asset.',
    'example': '',
    'sequence': 7,
    'show_more': true,
    'lookup': null,
    'errorMessage': 'File not found in DAM',
    'is_pag': true,
    'validation': {
      'pies65' : {
        required: true,
        message: 'File name is required.'
      }
    },
  },
  {
    'field_map': 'width',
    'item_name': 'width',
    'title': 'Asset width',
    'hint_detail': 'Horizontal measurement of digital asset file.',
    'hint_panel' : 'Horizontal measurement of digital asset file.',
    'example': '',
    'sequence': 8,
    'show_more': true,
    'lookup': null,
    'imageOnly': true,
  },
  {
    'field_map': 'height',
    'item_name': 'height',
    'title': 'Asset height',
    'hint_detail': 'Vertical measurement of digital asset file.',
    'hint_panel' : 'Vertical measurement of digital asset file.',
    'example': '',
    'sequence': 9,
    'show_more': true,
    'lookup': null,
    'imageOnly': true,
  },
  {
    'field_map': 'background',
    'item_name': 'background',
    'title': 'Background',
    'hint_detail': 'Indicates what type of background is in the photo.',
    'hint_panel' : 'Select what type of background is in the photo.',
    'example': '',
    'sequence': 10,
    'show_more': true,
    'lookup': backgroundLookup,
    'imageOnly': true,
  },
  {
    'field_map': 'orientationView',
    'item_name': 'orientation_view',
    'title': 'Orientation view',
    'hint_detail': 'Indicates the angle or orientation of the photo. User-defined orientations are maintained at Global account level.',
    'hint_panel' : 'Select the angle or orientation of this photo. User-defined orientations are maintained at Global account level.',
    'example': '',
    'sequence': 11,
    'show_more': true,
    'lookup': orientationViewLookup,
    'imageOnly': true,
  },
  {
    'field_map': 'filePath',
    'item_name': 'file_path',
    'title': 'URL',
    'hint_detail': 'URL of the digital asset.',
    'hint_panel' : '',
    'example': '',
    'sequence': 12,
    'show_more': true,
    'lookup': null,
    'is_pag': true,
    'validation': {
      'pies65' : {
        required: true,
        pattern: patterns.anyURI,
        message: 'Invalid URL.'
      }
    },
  },
  {
    'field_map': 'description',
    'item_name': 'description',
    'title': 'File description',
    'hint_detail': 'General description for the use of the digital asset.',
    'hint_panel' : 'Enter up to 80 characters for a general description of use of the digital asset.',
    'example': 'For use on websites, brochures, and mailers.',
    'sequence': 13,
    'show_more': true,
    'lookup': null
  },
  {
    'field_map': 'uri',
    'item_name': 'uri',
    'title': 'URL',
    'hint_detail': 'File url for this digital asset.',
    'hint_panel' : '',
    'example': '',
    'sequence': 14,
    'show_more': true,
    'lookup': null
  },
  {
    'field_map': 'fileDateModified',
    'item_name': 'file_date_modified',
    'title': 'File date modified',
    'hint_detail': 'Modified date of this digital asset.',
    'hint_panel' : '',
    'example': '',
    'sequence': 15,
    'show_more': true,
    'lookup': null
  },
  {
    'field_map': 'effectiveDate',
    'item_name': 'effective_date',
    'title': 'Effective date',
    'hint_detail': 'Date for when this digital asset is in effect.',
    'hint_panel' : 'Enter date for when this digital asset is in effect.',
    'example': '01/01/2017',
    'sequence': 16,
    'show_more': true,
    'lookup': null,
    'type': DetailsControlType.Date,
    'validation': {
      'PIES_6_5': {
        message: 'Effective date cannot be set ahead of Expiration date.',
      }
    }
  },
  {
    'field_map': 'uom',
    'item_name': 'uom',
    'title': 'Unit of measurement',
    'hint_detail': 'Identifies pixels, inches or other measurement units for use with various Asset Types',
    'hint_panel' : 'Identifies pixels, inches or other measurement units for use with various Asset Types',
    'example': '',
    'sequence': 17,
    'show_more': true,
    'lookup': assetUomLookup,
    'imageOnly': true,
  },
  {
    'field_map': 'expirationDate',
    'item_name': 'expiration_date',
    'title': 'Expiration date',
    'hint_detail': 'Date for when this digiital asset should no longer be used.',
    'hint_panel' : 'Enter date for when this digiital asset should no longer be used. Expiration dates are not commonly used.',
    'example': '01/01/2017',
    'sequence': 18,
    'show_more': true,
    'type': DetailsControlType.Date,
    'lookup': null
  },
  {
    'field_map': 'totalAssignments',
    'item_name': 'total_assignments',
    'title': 'Assignment',
    'hint_detail': '',
    'hint_panel' : '',
    'example': '',
    'sequence': 19,
    'show_more': true,
    'lookup': null
  },
  {
    'field_map': 'country',
    'item_name': 'country',
    'title': 'Country',
    'hint_detail': 'Country',
    'hint_panel' : 'Country',
    'example': '',
    'sequence': 20,
    'show_more': true,
    'lookup': countryLookup,
  },
  {
    'field_map': 'language',
    'item_name': 'language',
    'title': 'Language',
    'hint_detail': 'Brand',
    'hint_panel' : '',
    'example': '',
    'sequence': 21,
    'show_more': true,
     "lookup": languageLookup,
  },
  {
    'field_map': 'additionalInfo',
    'item_name': 'additional_info',
    'title': 'Additional information',
    'hint_detail': 'Additional information',
    'hint_panel' : 'Additional information',
    'example': '',
    'sequence': 22,
    'show_more': true,
    'lookup': null
  },
  {
    'field_map': '_id',
    'item_name': '_id',
    'title': 'Id',
    'hint_detail': '',
    'hint_panel' : '',
    'example': '',
    'sequence': 23,
    'show_more': true,
    'lookup': null
  },
  {
    'field_map': 'originId',
    'item_name': 'origin_id',
    'title': 'Origin Id',
    'hint_detail': '',
    'hint_panel' : '',
    'example': '',
    'sequence': 24,
    'show_more': true,
    'lookup': null
  },
  {
    'field_map': 'systemFilename',
    'item_name': 'system_filename',
    'title': 'System filename',
    'hint_detail': '',
    'hint_panel' : '',
    'example': '',
    'sequence': 25,
    'show_more': true,
    'lookup': null
  },
  {
    'field_map': 'digitalAssetId',
    'item_name': 'digital_asset_id',
    'title': 'Digital asset ID',
    'hint_detail': '',
    'hint_panel' : '',
    'example': '',
    'sequence': 26,
    'show_more': true,
    'lookup': null
  },
  {
    "field_map": "dimensionsUom",
    "item_name": "uom",
    "title": "Dimensions unit of measurement",
    "hint_detail": "Units of measurement for digital asset dimensions.",
    "hint_panel" : "Choose the unit of measurement for digital asset dimensions.",
    "example": "Pixels",
    "sequence": 7,
    "show_more": true,
    'validation': {
      'pies65' : {
        required: true,
        message: 'Dimensions unit of measurement is required, when setting Width and Height'
      }
    },
    "lookup": [
      {
        "option": "",
        "title": "-"
      },
      {
        "option": "PX",
        "title": "Pixels"
      },
      {
        "option": "IN",
        "title": "Inches"
      },
      {
        "option": "CM",
        "title": "Centimeters"
      }
    ]
  },
  {
    'field_map': 'thirdPartyAsset',
    'item_name': 'third_party_asset',
    'title': 'Asset Hosted Externally',
    'hint_detail': 'Asset Hosted Externally.',
    'hint_panel' : 'Asset Hosted Externally.',
    'example': '',
    'sequence': 28,
    'show_more': true,
    'lookup': [
      {
        'option': 0,
        'title': 'No'
      },
      {
        'option': 1,
        'title': 'Yes'
      }
    ]
  },

  {
    'field_map': 'frame',
    'item_name': 'frame',
    'title': 'Frame',
    'hint_detail': 'Number of the image frame',
    'hint_panel' : 'Number of the image frame',
    'example': '',
    'sequence': 30,
    'show_more': true,
    'lookup': null
  },
  {
    'field_map': 'totalFrame',
    'item_name': 'total_frame',
    'title': 'Total Frame',
    'hint_detail': 'Total number of frames within a plane',
    'hint_panel' : 'Total number of frames within a plane',
    'example': '',
    'sequence': 31,
    'show_more': true,
    'lookup': null
  },
  {
    'field_map': 'hemisphere',
    'item_name': 'hemisphere',
    'title': 'Hemisphere',
    'hint_detail': 'Either Northern (N) or Southern (S)',
    'hint_panel' : 'Either Northern (N) or Southern (S)',
    'example': '',
    'sequence': 32,
    'show_more': true,
    'lookup': [
      {
        'option': 'N',
        'title': 'North'
      },
      {
        'option': 'S',
        'title': 'South'
      }
    ]
  },
  {
    'field_map': 'plunge',
    'item_name': 'plunge',
    'title': 'Plunge',
    'hint_detail': 'Plunge angle when photographed',
    'hint_panel' : 'Plunge angle when photographed',
    'example': '',
    'sequence': 33,
    'show_more': true,
    'lookup': null
  },
  {
    'field_map': 'plane',
    'item_name': 'plane',
    'title': 'Plane',
    'hint_detail': 'Number of planes that are in the 3D image set',
    'hint_panel' : 'Number of planes that are in the 3D image set',
    'example': '',
    'sequence': 34,
    'show_more': true,
    'lookup': null
  },
  {
    'field_map': 'totalPlanes',
    'item_name': 'total_planes',
    'title': 'Total Planes',
    'hint_detail': 'Number of planes for a 360 degree image',
    'hint_panel' : 'Number of planes for a 360 degree image',
    'example': '',
    'sequence': 35,
    'show_more': true,
    'lookup': null
  },
  {
    'field_map': 'duration',
    'item_name': 'duration',
    'title': 'Duration',
    'hint_detail': 'Video Duration',
    'hint_panel' : 'Video Duration',
    'example': '',
    'sequence': 36,
    'show_more': true,
    'lookup': null
  },
  {
    'field_map': 'durationUom',
    'item_name': 'duration_uom',
    'title': 'Duration UOM',
    'hint_detail': 'Duration unit of measurement',
    'hint_panel' : 'Duration unit of measurement',
    'example': '',
    'sequence': 37,
    'show_more': true,
    'lookup': [
      {
        'option': 'hh',
        'title': 'Hours'
      },
      {
        'option': 'mm',
        'title': 'Minutes'
      },
      {
        'option': 'ss',
        'title': 'Seconds'
      }
    ]
  },
  {
    'field_map': 'descriptions',
    'item_name': 'descriptions',
    'title': '',
    'hint_detail': '',
    'hint_panel' : '',
    'example': {},
    'sequence': 38,
    'show_more': true,
    'lookup': null
  },
];
/* eslint-enable */
